/* Feb-2026 · Publishing-Election checkbox card for §2.6.
 *
 * Small card rendered on the manuscript / bookstore-listing form
 * (also usable on Business Dashboard per project). Lets the author
 * toggle two flags — publish through Crown & Rose's Amazon KDP
 * account and/or through the Crown & Rose Google Play Books account.
 *
 * Backend enforces that both flags require an executed Publishing
 * Agreement on the current version (§2.6 wording lives on v1.1+).
 * When the author isn't there yet, the API returns a 409 with a clear
 * message that we surface as a red banner.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { BookOpenCheck, Loader2, Check, AlertTriangle } from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function PublishingElectionCard({ projectId }) {
  const { t } = useTranslation();
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [kdp,     setKdp]       = useState(false);
  const [gplay,   setGplay]     = useState(false);
  const [blocker, setBlocker]   = useState(null);
  const [elected, setElected]   = useState(null);

  const load = async () => {
    setLoading(true); setBlocker(null);
    try {
      const r = await http.get(`/projects/${projectId}/publishing-election`);
      const pe = r.data.publishing_election || {};
      setKdp(!!pe.kdp); setGplay(!!pe.google_play);
      setElected(pe.elected_at || null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setBlocker(typeof d === 'string' ? d : d?.message || t('publish.electionLoadFailed'));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);   // eslint-disable-line

  const save = async () => {
    setSaving(true); setBlocker(null);
    try {
      const r = await http.post(`/projects/${projectId}/publishing-election`, {
        kdp, google_play: gplay,
      });
      setElected(r.data.publishing_election?.elected_at || new Date().toISOString());
      toast.success(t('publish.electionSaved'));
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : d?.message || t('publish.electionSaveFailed');
      setBlocker(msg);
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!projectId) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="publishing-election-card">
      <div className="flex items-center gap-2 text-[13px] font-semibold tracking-tight">
        <BookOpenCheck className="h-4 w-4 text-primary" />
        {t('publish.electionTitle')}
        {elected && (
          <span className="ml-auto text-[10.5px] text-muted-foreground">
            <Check className="mr-0.5 inline h-3 w-3 text-emerald-600" />
            {t('publish.electionElected', { date: elected.slice(0, 10) })}
          </span>
        )}
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
        {t('publish.electionBody')}
      </p>
      {loading ? (
        <div className="mt-3 text-[11px] text-muted-foreground">
          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> {t('publish.loading')}
        </div>
      ) : (
        <div className="mt-3 grid gap-2">
          <label className="flex items-center gap-2 text-[12px]" data-testid="publishing-election-kdp-row">
            <input
              type="checkbox" checked={kdp}
              onChange={(e) => setKdp(e.target.checked)}
              data-testid="publishing-election-kdp"
            />
            {t('publish.electionKdp')}
          </label>
          <label className="flex items-center gap-2 text-[12px]" data-testid="publishing-election-gp-row">
            <input
              type="checkbox" checked={gplay}
              onChange={(e) => setGplay(e.target.checked)}
              data-testid="publishing-election-gp"
            />
            {t('publish.electionGooglePlay')}
          </label>
          {blocker && (
            <div
              data-testid="publishing-election-blocker"
              className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 p-2 text-[11.5px] text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              <span>{blocker}</span>
            </div>
          )}
          <div>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="publishing-election-save"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('publish.electionSaving')}</> : t('publish.electionSave')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
